import {useCallback, useMemo, useState} from "react";
import styled from "styled-components";
import type {ITextInputProps} from "../../../widgets/input/text/TextInput";
import {TextInputV5} from "../../details/datatypes/TextInputV5";
import {PillsStyle} from "../../sharing/SharingPanel.styled";
import {Permission} from "../../../../generated/api/base";
import {colorPalette} from "../../styles/colorPalette";
import {fontSize, fontWeight, radius} from "../../styles/styles";
import {SelectInputV5} from "../select/SelectInputV5";
import {HorizontalAlignment} from "../../../../utils/dom/DomUtils";
import CloseIcon from "../../icons/xmark.svg?react";
import type {SelectedItem} from "../../sharing/SharingUtils";

interface ISearchFieldV5Props extends Partial<ITextInputProps> {
	readonly value: string;
	readonly placeholder: string;
	readonly selectedItems: SelectedItem[];
	readonly isDropdown?: boolean;
	readonly isSharing?: boolean;
	readonly dropdownDisabled?: boolean;
	readonly hasEdit?: boolean;
	readonly setPermissionType?: (permission: Permission) => void;
	readonly removeSelectedItem?: (items: SelectedItem[]) => void;
}

export const SelectSearchFieldV5 = (props: ISearchFieldV5Props) => {
	const [selectedPermissionType, setSelectedPermissionType] = useState<Permission>(Permission.View);
	const {placeholder = "Find", selectedItems, isDropdown, removeSelectedItem, dropdownDisabled, setPermissionType, hasEdit = true} = props;

	const permissionOptions = useMemo(() => {
		const options = [{value: Permission.View, label: "Can View"}];

		// For some modules (Report), we want to not show Edit option when sharing with a non-admin
		if (hasEdit) {
			options.push({value: Permission.Update, label: "Can Edit"});
		}

		return options;
	}, [hasEdit]);

	const selectedPermissionLabel = permissionOptions.find((option) => option.value === selectedPermissionType)?.label || "";

	const handleRemoveSelectedItem = (id: string) => {
		removeSelectedItem(selectedItems.filter((item) => item.id !== id));
	};

	const onSetPermission = useCallback(
		(selectedOption: {value: Permission; label: string}) => {
			setSelectedPermissionType(selectedOption.value);
			setPermissionType(selectedOption.value);
		},
		[setPermissionType],
	);

	const selectedOption = permissionOptions.find((option) => option.value === selectedPermissionType);

	// The selected permission can become unavailable (eg.: Edit, when a report is shared with non-admins), so fall back to View.
	// The parent resets its own permission the same way, so it doesn't need to be notified here
	if (!selectedOption) {
		setSelectedPermissionType(permissionOptions[0].value);
	}

	return (
		<SelectSearchFieldStyled
			className="SearchField"
			$multiselectDropdown={true}
		>
			<div className="inputField">
				{selectedItems.map((item) => (
					<PillsStyle
						className="selectedItem"
						key={item.id}
						$color={colorPalette.gray.c100}
					>
						<span>{item.itemName}</span>
						<CloseIcon onClick={() => handleRemoveSelectedItem(item.id)} />
					</PillsStyle>
				))}
				<div className="list-search">
					<TextInputV5
						{...props}
						placeholder={selectedItems.length > 0 ? "" : placeholder || "Find"}
					/>
				</div>
			</div>
			{!isDropdown ? (
				""
			) : (
				<div className="permissionSelect">
					{dropdownDisabled ? (
						<div className="permissionSelectInput disabledPermissionLabel">{selectedPermissionLabel}</div>
					) : (
						<SelectInputV5
							options={permissionOptions}
							renderOptionWhenClosed={(option) => option.label}
							selected={selectedOption}
							onChange={onSetPermission}
							placeholder={selectedPermissionLabel}
							className="permissionSelectInput"
							dropdownFixedWidth="128px"
							optionFontSize="14px"
							horizontalAlignment={HorizontalAlignment.right}
						/>
					)}
				</div>
			)}
		</SelectSearchFieldStyled>
	);
};

export const SelectSearchFieldStyled = styled.div<{$multiselectDropdown?: boolean}>`
	border-radius: ${radius.sm};
	background-color: ${colorPalette.white};
	border: 1px solid ${colorPalette.gray.c300};
	align-items: center;
	position: relative;
	font-size: ${fontSize.md};
	display: flex;
	justify-content: space-between;
	align-items: center;
	gap: 8px;
	flex: 1;

	.inputField {
		display: inline-flex;
		flex-wrap: wrap;
		align-items: center;
		gap: 8px;
		flex: 1;
		padding: 3px;

		.list-search {
			height: 24px;
			min-width: 10px;
			display: flex;
			flex: 1;
		}
	}

	input {
		border: none;
		background-color: transparent;
		font-weight: ${fontWeight.normal};
		font-size: ${fontSize.md};
		position: relative;
		min-width: 10px;
		height: 24px;

		&:focus-visible {
			outline: none;
		}
	}

	.permissionSelect {
		width: 96px;
		height: 30px;
	}

	svg {
		width: 32px;
		height: 16px;
		min-width: 16px;
		min-height: 16px;
		left: 8px;

		&.cancel {
			left: auto;
			right: 8px;
			cursor: pointer;
		}

		path {
			width: 12px;
			height: 12px;
		}
	}

	.saveDropdown {
		width: 90px;

		svg {
			margin: 0;
			width: 16px;
		}
	}

	.permissionSelectInput {
		border: none;
		font-size: ${fontSize.sm};
		padding-left: 8px;
		height: 30px;

		svg {
			width: 5px;
		}
	}

	.disabledPermissionLabel {
		display: flex;
		align-items: center;
		height: 100%;
		cursor: default;
		pointer-events: none;
	}
`;

export const SelectedOptionStyled = styled.div`
	top: 0px;
	left: 15px;
`;
